import React, { useState, useEffect } from "react";
import { PageStructure } from "../../pages";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Select from "react-select";
import {
  Loader,
  AddChargeButton,
  PopUpWindow,
  ChargeForm,
  InvoiceForm,
  InvoiceTable,
  TransactionTable,
  PrimaryButton,
  NewFundTransactionForm,
} from "../../components";
import apiUrl from "../../components/api";
import axios from "axios";
import AddInvoiceButton from "../../components/addInvoiceButton/AddInvoiceButton";
import io from "socket.io-client";

// Utility function to generate a color for an avatar based on name
const getColor = (name) => {
  if (!name) return "#cccccc";
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 100%, 30%)`;
  return color;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
};

// TabPanel component to handle tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`property-tabpanel-${index}`}
      aria-labelledby={`property-tab-${index}`}
      {...other}
      style={{ width: "100%" }}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Inner TabPanel component for financial data tabs
function FinanceTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`finance-tabpanel-${index}`}
      aria-labelledby={`finance-tab-${index}`}
      {...other}
      style={{ width: "100%" }}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Function to get aria-controls property for tabs
function a11yProps(index) {
  return {
    id: `property-tab-${index}`,
    "aria-controls": `property-tabpanel-${index}`,
  };
}

// Function to get aria-controls property for inner finance tabs
function financeTabProps(index) {
  return {
    id: `finance-tab-${index}`,
    "aria-controls": `finance-tabpanel-${index}`,
  };
}

const PreviewOwner = () => {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
  const [loading, setLoading] = useState(true);
  const [ownersLoading, setOwnersLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [financeTabValues, setFinanceTabValues] = useState({});
  const [allPropertiesTabValue, setAllPropertiesTabValue] = useState(0);
  const [selectedPropertyForCharge, setSelectedPropertyForCharge] =
    useState(null);
  const [propertyCharges, setPropertyCharges] = useState({}); // Store charges for each property
  const [chargesLoading, setChargesLoading] = useState({}); // Track loading state for each property's charges
  // New state variables for viewing charge details
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [chargeViewOpen, setChargeViewOpen] = useState(false);
  // New state variables for invoice form popup
  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);
  const [selectedChargeForInvoice, setSelectedChargeForInvoice] =
    useState(null);
  // Add state for multiple charge selection
  const [selectedCharges, setSelectedCharges] = useState([]);

  // Add state for payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedChargeForPayment, setSelectedChargeForPayment] =
    useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const [error, setError] = useState("");
  const [fundTransactionPopupOpen, setFundTransactionPopupOpen] =
    useState(false);

  // Add socket connection
  useEffect(() => {
    const socket = io(apiUrl);

    // Listen for charge updates
    socket.on("charge", (data) => {
      if (data.action === "create" || data.action === "update") {
        const charge = data.charge;
        if (charge.property) {
          const propertyId =
            typeof charge.property === "object"
              ? charge.property._id
              : charge.property;
          setPropertyCharges((prev) => {
            const currentCharges = prev[propertyId] || [];
            if (data.action === "create") {
              return {
                ...prev,
                [propertyId]: [...currentCharges, charge],
              };
            } else {
              return {
                ...prev,
                [propertyId]: currentCharges.map((c) =>
                  c._id === charge._id ? charge : c
                ),
              };
            }
          });
        }
      } else if (data.action === "delete") {
        const chargeId = data.chargeId;
        setPropertyCharges((prev) => {
          const newCharges = {};
          Object.keys(prev).forEach((propertyId) => {
            newCharges[propertyId] = prev[propertyId].filter(
              (c) => c._id !== chargeId
            );
          });
          return newCharges;
        });
      }
    });

    // Listen for owner balance updates
    socket.on("user", (data) => {
      if (
        data.action === "updateBalance" &&
        data.user &&
        selectedOwner === data.user._id
      ) {
        setOwnerDetails((prev) => ({
          ...prev,
          balance: data.user.balance,
        }));
      }
    });

    // Listen for contract updates that affect charges
    socket.on("contract", (data) => {
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        // Refresh owner details to reflect any changes in charges
        if (selectedOwner) {
          fetchOwnerDetails(selectedOwner);
        }
      }
    });

    // Listen for action updates that affect charges
    socket.on("actionCreated", (action) => {
      if (action.property) {
        const propertyId =
          typeof action.property === "object"
            ? action.property._id
            : action.property;
        if (propertyCharges[propertyId]) {
          fetchPropertyCharges(propertyId);
        }
      }
    });

    socket.on("actionUpdated", (action) => {
      if (action.property) {
        const propertyId =
          typeof action.property === "object"
            ? action.property._id
            : action.property;
        if (propertyCharges[propertyId]) {
          fetchPropertyCharges(propertyId);
        }
      }
    });

    socket.on("actionDeleted", (actionId) => {
      // Refresh all property charges to ensure consistency
      if (ownerDetails?.properties) {
        ownerDetails.properties.forEach((property) => {
          if (propertyCharges[property._id]) {
            fetchPropertyCharges(property._id);
          }
        });
      }
    });

    // Listen for pool action updates
    socket.on("poolAction", (data) => {
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        const propertyId =
          data.poolAction?.property?._id || data.poolAction?.property;
        if (propertyId && propertyCharges[propertyId]) {
          fetchPropertyCharges(propertyId);
        }
      }
    });

    // Cleanup socket listeners on component unmount
    return () => {
      socket.off("charge");
      socket.off("user");
      socket.off("contract");
      socket.off("actionCreated");
      socket.off("actionUpdated");
      socket.off("actionDeleted");
      socket.off("poolAction");
      socket.disconnect();
    };
  }, [selectedOwner, ownerDetails?.properties, propertyCharges]);

  // Handler for viewing charge details
  const handleViewCharge = (charge) => {
    setSelectedCharge(charge);
    setChargeViewOpen(true);
  };

  // Handler for closing charge view popup
  const handleCloseChargeView = () => {
    setChargeViewOpen(false);
    setSelectedCharge(null);
  };

  // Handler for creating invoice from charge
  const handleCreateInvoice = (charge) => {
    setSelectedChargeForInvoice(charge);
    setInvoiceFormOpen(true);
  };

  // Handler for closing invoice form popup
  const handleCloseInvoiceForm = () => {
    setInvoiceFormOpen(false);
    setSelectedChargeForInvoice(null);
  };

  // Handler for successful invoice creation
  const handleInvoiceCreated = (newInvoice) => {
    handleCloseInvoiceForm();

    // Refresh charges for this property
    if (selectedChargeForInvoice && selectedChargeForInvoice.property) {
      const propertyId =
        typeof selectedChargeForInvoice.property === "object"
          ? selectedChargeForInvoice.property._id
          : selectedChargeForInvoice.property;

      fetchPropertyCharges(propertyId);
    }

    // Also refresh all owner details to reflect changes
    fetchOwnerDetails(selectedOwner);
  };

  // Add handlers for multiple charge selection
  const handleChargeSelect = (charge, isSelected) => {
    if (isSelected) {
      setSelectedCharges([...selectedCharges, charge]);
    } else {
      setSelectedCharges(selectedCharges.filter((c) => c._id !== charge._id));
    }
  };

  const handleCreateInvoiceFromMultipleCharges = () => {
    // Check if all selected charges have the same property
    const allSameProperty = selectedCharges.every((charge) => {
      const propertyId =
        typeof charge.property === "object"
          ? charge.property._id
          : charge.property;
      const firstPropertyId =
        typeof selectedCharges[0].property === "object"
          ? selectedCharges[0].property._id
          : selectedCharges[0].property;
      return propertyId === firstPropertyId;
    });

    if (!allSameProperty) {
      setError("Cannot create invoice with charges from different properties");
      return;
    }

    // Create an invoice with multiple charges
    setSelectedChargeForInvoice({
      ...selectedCharges[0],
      multipleCharges: selectedCharges,
    });
    setInvoiceFormOpen(true);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // When changing tabs, update the selected property for charge
    if (newValue === 0) {
      // All properties tab
      setSelectedPropertyForCharge(null);
    } else if (
      ownerDetails?.properties &&
      ownerDetails.properties[newValue - 1]
    ) {
      // Individual property tab
      const property = ownerDetails.properties[newValue - 1];
      setSelectedPropertyForCharge(property);

      // Fetch charges for the selected property if we haven't already
      if (!propertyCharges[property._id]) {
        fetchPropertyCharges(property._id);
      }
    }
  };

  // Handle finance tab change for a specific property
  const handleFinanceTabChange = (propertyId, newValue) => {
    setFinanceTabValues((prev) => ({
      ...prev,
      [propertyId]: newValue,
    }));

    // If changing to charges tab (index 0) and charges haven't been fetched yet, fetch them
    if (newValue === 0 && !propertyCharges[propertyId]) {
      fetchPropertyCharges(propertyId);
    }
  };

  // Handle tab change for all properties finance tabs
  const handleAllPropertiesTabChange = (event, newValue) => {
    setAllPropertiesTabValue(newValue);

    // If changing to charges tab (index 0), ensure we've fetched charges for all properties
    if (newValue === 0 && ownerDetails?.properties) {
      ownerDetails.properties.forEach((property) => {
        if (!propertyCharges[property._id]) {
          fetchPropertyCharges(property._id);
        }
      });
    }
  };

  // Fetch charges for a specific property
  const fetchPropertyCharges = async (propertyId) => {
    try {
      // Set loading state for this property's charges
      setChargesLoading((prev) => ({ ...prev, [propertyId]: true }));

      const response = await axios.get(
        `${apiUrl}/charge/property/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      // Store the charges for this property
      setPropertyCharges((prev) => ({
        ...prev,
        [propertyId]: response.data.charges,
      }));

      // Update loading state
      setChargesLoading((prev) => ({ ...prev, [propertyId]: false }));
    } catch (err) {
      console.error(`Failed to fetch charges for property ${propertyId}:`, err);
      setError(
        `Failed to fetch charges for property. ${
          err.response?.data?.message || err.message
        }`
      );
      setChargesLoading((prev) => ({ ...prev, [propertyId]: false }));
    }
  };

  // Fetch the list of owners (users with role "owner") for the dropdown
  useEffect(() => {
    const fetchOwners = async () => {
      try {
        setOwnersLoading(true);
        const response = await axios.get(`${apiUrl}/auth/owners`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        setOwners(response.data.owners);
        setOwnersLoading(false);
      } catch (error) {
        setError("Failed to fetch owners. Please try again.");
        setOwnersLoading(false);
      }
    };
    fetchOwners();
  }, []);

  const handleChargeAdded = (newCharge) => {
    // Refresh charges for this property
    if (newCharge.property) {
      fetchPropertyCharges(newCharge.property);
    }

    // Also refresh all owner details to reflect changes
    fetchOwnerDetails(selectedOwner);
  };

  // Fetch details of the selected owner
  const fetchOwnerDetails = async (ownerId) => {
    if (!ownerId) {
      setLoading(false);
      setOwnerDetails(null);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/auth/owner/${ownerId}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      setOwnerDetails(response.data.owner);

      // Initialize finance tabs for each property
      if (response.data.owner.properties) {
        const initialFinanceTabValues = {};
        response.data.owner.properties.forEach((property) => {
          initialFinanceTabValues[property._id] = 0;

          // Fetch charges for each property
          fetchPropertyCharges(property._id);
        });
        setFinanceTabValues(initialFinanceTabValues);

        // Set the selected property if we're on a property tab
        if (tabValue > 0 && tabValue <= response.data.owner.properties.length) {
          setSelectedPropertyForCharge(
            response.data.owner.properties[tabValue - 1]
          );
        } else {
          setSelectedPropertyForCharge(null);
        }
      }

      setLoading(false);
    } catch (err) {
      setError("Failed to fetch owner details. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOwnerDetails(selectedOwner);

    // Clear property charges when owner changes
    setPropertyCharges({});
    setChargesLoading({});
  }, [selectedOwner]);

  const handleOwnerSelect = (selectedOption) => {
    setSelectedOwner(selectedOption?.value);
    // Reset tabs when changing owner
    setTabValue(0);
    setAllPropertiesTabValue(0);
    setFinanceTabValues({});
  };

  return (
    <PageStructure>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Preview Owner
        </Typography>

        <Box sx={{ maxWidth: 500, mb: 4 }}>
          {ownersLoading ? (
            <Loader />
          ) : (
            <Select
              options={owners.map((owner) => ({
                value: owner._id,
                label: `${owner.name} - ${
                  owner.tax_id || "No Tax ID"
                } - ${owner.properties.map((property) => property.name)}`,
              }))}
              placeholder="Select an owner to preview..."
              onChange={handleOwnerSelect}
              isSearchable
              isClearable
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100001 }),
                menu: (base) => ({ ...base, zIndex: 100001 }),
              }}
            />
          )}
        </Box>

        {loading ? (
          <Loader />
        ) : ownerDetails ? (
          <Box>
            <Card sx={{ width: "100%", mb: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: getColor(ownerDetails.name),
                      width: 64,
                      height: 64,
                      fontSize: 32,
                      mr: 2,
                    }}
                  >
                    {ownerDetails.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" component="h2">
                      {ownerDetails.name}
                    </Typography>
                    <Chip
                      label="Owner"
                      color="primary"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Username
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.username}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.email}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.phone || "Not provided"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Tax ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.tax_id || "Not provided"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      AMKA
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.amka || "Not provided"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      IBAN
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ownerDetails.iban || "Not provided"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Balance
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{
                        color: ownerDetails.balance <= 0 ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {formatCurrency(ownerDetails.balance || 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Companies section */}
            {ownerDetails.companies && ownerDetails.companies.length > 0 && (
              <Card sx={{ width: "100%", mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Associated Companies
                  </Typography>

                  {ownerDetails.companies.length > 0 ? (
                    <Grid container spacing={2}>
                      {ownerDetails.companies.map((company, index) => (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {company.name}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Tax ID:
                                </Typography>
                                <Typography variant="body2">
                                  {company.taxId}
                                </Typography>
                              </Grid>

                              {company.iban && (
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    IBAN:
                                  </Typography>
                                  <Typography variant="body2">
                                    {company.iban}
                                  </Typography>
                                </Grid>
                              )}
                              {company.notes && (
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Notes:
                                  </Typography>
                                  <Typography variant="body2">
                                    {company.notes}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography>No company details available</Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Properties section */}
            {ownerDetails.properties && ownerDetails.properties.length > 0 && (
              <Card sx={{ width: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">Properties</Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {/* <AddInvoiceButton
                        propertyId={selectedPropertyForCharge?._id}
                        onInvoiceAdded={handleChargeAdded}
                      /> */}

                      {user.permissions.canDecideTransaction && (
                        <Tooltip title="Create Fund Transaction">
                          <PrimaryButton
                            text="Create Fund Transaction"
                            onClick={() => setFundTransactionPopupOpen(true)}
                          >
                            <NewFundTransactionForm
                              onSuccess={() =>
                                setFundTransactionPopupOpen(false)
                              }
                              partner={ownerDetails}
                            />
                          </PrimaryButton>
                        </Tooltip>
                      )}

                      {user.permissions.canAddCharge && (
                        <AddChargeButton
                          selectedProperty={selectedPropertyForCharge}
                          onChargeAdded={handleChargeAdded}
                        />
                      )}

                      {user.permissions.canAddInvoice && (
                        <Tooltip title="Create Invoice from Selected Charges">
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<AddIcon />}
                            onClick={handleCreateInvoiceFromMultipleCharges}
                            disabled={
                              selectedCharges.length === 0 ||
                              selectedCharges.some((charge) => charge.invoice)
                            }
                            sx={{ mb: 2 }}
                          >
                            Create Invoice
                          </Button>

                          {/* <IconButton
                          onClick={handleCreateInvoiceFromMultipleCharges}
                          disabled={selectedCharges.length === 0}
                        >
                          <AddIcon />
                        </IconButton> */}
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {ownerDetails.properties.length > 0 ? (
                    <Box sx={{ width: "100%" }}>
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <Tabs
                          value={tabValue}
                          onChange={handleTabChange}
                          aria-label="property tabs"
                          variant="scrollable"
                          scrollButtons="auto"
                        >
                          <Tab label="All Properties" {...a11yProps(0)} />
                          {ownerDetails.properties.map((property, index) => (
                            <Tab
                              key={property._id}
                              label={property.name}
                              {...a11yProps(index + 1)}
                            />
                          ))}
                        </Tabs>
                      </Box>

                      {/* All Properties Tab */}
                      <TabPanel value={tabValue} index={0}>
                        <Typography variant="h6" gutterBottom>
                          Overview of All Properties
                        </Typography>

                        {/* Replace table with tabs */}
                        <Box sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              borderBottom: 1,
                              borderColor: "divider",
                              mb: 2,
                            }}
                          >
                            <Tabs
                              value={allPropertiesTabValue}
                              onChange={handleAllPropertiesTabChange}
                              aria-label="all properties financial tabs"
                            >
                              <Tab label="Charges" {...financeTabProps(0)} />
                              <Tab label="Invoices" {...financeTabProps(1)} />
                              <Tab
                                label="Transactions"
                                {...financeTabProps(2)}
                              />
                            </Tabs>
                          </Box>

                          {/* Charges Tab for All Properties */}
                          {user.permissions.canViewCharges && (
                            <FinanceTabPanel
                              value={allPropertiesTabValue}
                              index={0}
                            >
                              <Typography variant="subtitle1" gutterBottom>
                                All Charges
                              </Typography>

                              <TableContainer component={Paper}>
                                <Table aria-label="all charges table">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Select</TableCell>
                                      <TableCell>Property</TableCell>
                                      <TableCell>Date</TableCell>
                                      <TableCell>Amount</TableCell>
                                      <TableCell>Remaining</TableCell>
                                      <TableCell>Invoice #</TableCell>
                                      <TableCell>Notes</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {ownerDetails.properties.map((property) => {
                                      const charges =
                                        propertyCharges[property._id] || [];
                                      const isLoading =
                                        chargesLoading[property._id];

                                      if (isLoading) {
                                        return (
                                          <TableRow
                                            key={`loading-${property._id}`}
                                          >
                                            <TableCell colSpan={9}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "center",
                                                  p: 2,
                                                }}
                                              >
                                                <Loader size={30} />
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }

                                      if (charges.length === 0) {
                                        return (
                                          <TableRow
                                            key={`empty-${property._id}`}
                                          >
                                            <TableCell />
                                            <TableCell>
                                              {property.name}
                                            </TableCell>
                                            <TableCell colSpan={8}>
                                              No charges available
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }

                                      return charges.map((charge) => (
                                        <TableRow key={charge._id}>
                                          <TableCell>
                                            {!charge.invoice &&
                                              user.permissions.canEditCharge &&
                                              user.permissions
                                                .canAddInvoice && (
                                                <Checkbox
                                                  checked={selectedCharges.some(
                                                    (c) => c._id === charge._id
                                                  )}
                                                  onChange={(e) =>
                                                    handleChargeSelect(
                                                      charge,
                                                      e.target.checked
                                                    )
                                                  }
                                                />
                                              )}
                                          </TableCell>
                                          <TableCell>{property.name}</TableCell>
                                          <TableCell>
                                            {new Date(
                                              charge.created_at
                                            ).toLocaleDateString()}
                                          </TableCell>
                                          <TableCell>
                                            {formatCurrency(charge.amount)}
                                          </TableCell>
                                          <TableCell>
                                            {formatCurrency(
                                              charge.remaining_amount
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {charge.invoice
                                              ? charge.invoice.invoice_number
                                              : "-"}
                                          </TableCell>
                                          <TableCell
                                            style={{
                                              maxWidth: "150px",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {charge.notes ? (
                                              <Tooltip
                                                title={charge.notes}
                                                PopperProps={{
                                                  style: { zIndex: 10001 },
                                                  modifiers: [
                                                    {
                                                      name: "offset",
                                                      options: {
                                                        offset: [0, -10],
                                                      },
                                                    },
                                                  ],
                                                  sx: {
                                                    "& .MuiTooltip-tooltip": {
                                                      fontSize: "13px",
                                                    },
                                                  },
                                                }}
                                              >
                                                <span>
                                                  {charge.notes.substring(
                                                    0,
                                                    30
                                                  ) +
                                                    (charge.notes.length > 30
                                                      ? "..."
                                                      : "")}
                                                </span>
                                              </Tooltip>
                                            ) : (
                                              "-"
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={
                                                charge.paid ? "Paid" : "Unpaid"
                                              }
                                              color={
                                                charge.paid
                                                  ? "success"
                                                  : "warning"
                                              }
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Tooltip title="View Details">
                                              <IconButton
                                                onClick={() =>
                                                  handleViewCharge(charge)
                                                }
                                              >
                                                <RemoveRedEyeIcon />
                                              </IconButton>
                                            </Tooltip>
                                            {!charge.invoice && (
                                              <Tooltip title="Create Invoice">
                                                <IconButton
                                                  onClick={() =>
                                                    handleCreateInvoice(charge)
                                                  }
                                                >
                                                  <AddIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            {!charge.paid && (
                                              <Tooltip title="Pay Charge">
                                                <IconButton
                                                  color="success"
                                                  onClick={() => {
                                                    setSelectedChargeForPayment(
                                                      charge
                                                    );
                                                    setPaymentAmount("");
                                                    setPaymentError("");
                                                    setPaymentSuccess(false);
                                                    setPaymentDialogOpen(true);
                                                  }}
                                                >
                                                  <AttachMoneyIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ));
                                    })}

                                    {Object.keys(propertyCharges).length ===
                                      0 &&
                                      Object.keys(chargesLoading).length ===
                                        0 && (
                                        <TableRow>
                                          <TableCell colSpan={9} align="center">
                                            No charges available
                                          </TableCell>
                                        </TableRow>
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </FinanceTabPanel>
                          )}

                          {/* Invoices Tab for All Properties */}
                          {user.permissions.canViewInvoices && (
                            <FinanceTabPanel
                              value={allPropertiesTabValue}
                              index={1}
                            >
                              <Typography variant="subtitle1" gutterBottom>
                                All Invoices
                              </Typography>

                              {/* Pass a valid array of property IDs and ensure it's not empty */}
                              <InvoiceTable
                                propertyIds={
                                  ownerDetails?.properties
                                    ?.map((property) => property._id)
                                    .filter(Boolean) || []
                                }
                                onInvoiceUpdated={() =>
                                  fetchOwnerDetails(selectedOwner)
                                }
                              />
                            </FinanceTabPanel>
                          )}

                          {/* Transactions Tab for All Properties */}
                          {user.permissions.canDecideTransaction && (
                            <FinanceTabPanel
                              value={allPropertiesTabValue}
                              index={2}
                            >
                              <Typography variant="subtitle1" gutterBottom>
                                All Transactions
                              </Typography>

                              <TransactionTable
                                user={ownerDetails._id}
                                isMini
                              />
                            </FinanceTabPanel>
                          )}
                        </Box>
                      </TabPanel>

                      {/* Individual Property Tabs */}
                      {ownerDetails.properties.map((property, index) => (
                        <TabPanel
                          key={property._id}
                          value={tabValue}
                          index={index + 1}
                        >
                          {/* Inner tabs for financial information */}
                          <Box sx={{ width: "100%" }}>
                            <Box
                              sx={{
                                borderBottom: 1,
                                borderColor: "divider",
                                mb: 2,
                              }}
                            >
                              <Tabs
                                value={financeTabValues[property._id] || 0}
                                onChange={(e, newValue) =>
                                  handleFinanceTabChange(property._id, newValue)
                                }
                                aria-label={`${property.name} financial tabs`}
                              >
                                {user.permissions.canViewCharges && (
                                  <Tab
                                    label="Charges"
                                    {...financeTabProps(0)}
                                  />
                                )}
                                {user.permissions.canViewInvoices && (
                                  <Tab
                                    label="Invoices"
                                    {...financeTabProps(1)}
                                  />
                                )}
                                {user.permissions.canDecideTransaction && (
                                  <Tab
                                    label="Transactions"
                                    {...financeTabProps(2)}
                                  />
                                )}
                              </Tabs>
                            </Box>

                            {/* Charges Tab */}
                            {user.permissions.canViewCharges && (
                              <FinanceTabPanel
                                value={financeTabValues[property._id] || 0}
                                index={0}
                              >
                                <Typography variant="h6" gutterBottom>
                                  Charges for {property.name}
                                </Typography>

                                {chargesLoading[property._id] ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      p: 3,
                                    }}
                                  >
                                    <Loader />
                                  </Box>
                                ) : propertyCharges[property._id]?.length >
                                  0 ? (
                                  <TableContainer component={Paper}>
                                    <Table aria-label="charges table">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Select</TableCell>
                                          <TableCell>Date</TableCell>
                                          <TableCell>Amount</TableCell>
                                          <TableCell>Remaining</TableCell>
                                          <TableCell>Invoice #</TableCell>
                                          <TableCell>Notes</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {propertyCharges[property._id].map(
                                          (charge) => (
                                            <TableRow key={charge._id}>
                                              <TableCell>
                                                <Checkbox
                                                  checked={selectedCharges.some(
                                                    (c) => c._id === charge._id
                                                  )}
                                                  onChange={(e) =>
                                                    handleChargeSelect(
                                                      charge,
                                                      e.target.checked
                                                    )
                                                  }
                                                />
                                              </TableCell>
                                              <TableCell>
                                                {new Date(
                                                  charge.created_at
                                                ).toLocaleDateString()}
                                              </TableCell>
                                              <TableCell>
                                                {formatCurrency(charge.amount)}
                                              </TableCell>
                                              <TableCell>
                                                {formatCurrency(
                                                  charge.remaining_amount
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {charge.invoice
                                                  ? charge.invoice
                                                      .invoice_number
                                                  : "-"}
                                              </TableCell>
                                              <TableCell
                                                style={{
                                                  maxWidth: "150px",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {charge.notes ? (
                                                  <Tooltip
                                                    title={charge.notes}
                                                    PopperProps={{
                                                      style: { zIndex: 10001 },
                                                      modifiers: [
                                                        {
                                                          name: "offset",
                                                          options: {
                                                            offset: [0, -10],
                                                          },
                                                        },
                                                      ],
                                                      sx: {
                                                        "& .MuiTooltip-tooltip":
                                                          {
                                                            fontSize: "13px",
                                                          },
                                                      },
                                                    }}
                                                  >
                                                    <span>
                                                      {charge.notes.substring(
                                                        0,
                                                        30
                                                      ) +
                                                        (charge.notes.length >
                                                        30
                                                          ? "..."
                                                          : "")}
                                                    </span>
                                                  </Tooltip>
                                                ) : (
                                                  "-"
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={
                                                    charge.paid
                                                      ? "Paid"
                                                      : "Unpaid"
                                                  }
                                                  color={
                                                    charge.paid
                                                      ? "success"
                                                      : "warning"
                                                  }
                                                  size="small"
                                                />
                                              </TableCell>
                                              <TableCell>
                                                <Tooltip title="View Details">
                                                  <IconButton
                                                    onClick={() =>
                                                      handleViewCharge(charge)
                                                    }
                                                  >
                                                    <RemoveRedEyeIcon />
                                                  </IconButton>
                                                </Tooltip>
                                                {!charge.invoice && (
                                                  <Tooltip title="Create Invoice">
                                                    <IconButton
                                                      onClick={() =>
                                                        handleCreateInvoice(
                                                          charge
                                                        )
                                                      }
                                                    >
                                                      <AddIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                                {!charge.paid && (
                                                  <Tooltip title="Pay Charge">
                                                    <IconButton
                                                      color="success"
                                                      onClick={() => {
                                                        setSelectedChargeForPayment(
                                                          charge
                                                        );
                                                        setPaymentAmount("");
                                                        setPaymentError("");
                                                        setPaymentSuccess(
                                                          false
                                                        );
                                                        setPaymentDialogOpen(
                                                          true
                                                        );
                                                      }}
                                                    >
                                                      <AttachMoneyIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          )
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <Typography>
                                    No charges available for this property.
                                  </Typography>
                                )}
                              </FinanceTabPanel>
                            )}

                            {/* Invoices Tab */}
                            {user.permissions.canViewInvoices && (
                              <FinanceTabPanel
                                value={financeTabValues[property._id] || 0}
                                index={1}
                              >
                                <Typography variant="h6" gutterBottom>
                                  Invoices for {property.name}
                                </Typography>

                                {/* Replace with InvoiceTable component */}
                                <InvoiceTable
                                  propertyId={property._id}
                                  onInvoiceUpdated={() =>
                                    fetchOwnerDetails(selectedOwner)
                                  }
                                />
                              </FinanceTabPanel>
                            )}

                            {/* Transactions Tab */}
                            {user.permissions.canDecideTransaction && (
                              <FinanceTabPanel
                                value={financeTabValues[property._id] || 0}
                                index={2}
                              >
                                <Typography variant="h6" gutterBottom>
                                  Transactions for {property.name}
                                </Typography>

                                <TransactionTable
                                  property={property._id}
                                  user={ownerDetails._id}
                                  isMini
                                />
                              </FinanceTabPanel>
                            )}
                          </Box>
                        </TabPanel>
                      ))}
                    </Box>
                  ) : (
                    <Typography>No property details available</Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        ) : selectedOwner ? (
          <Typography color="error">
            {error || "Failed to load owner details."}
          </Typography>
        ) : (
          <Typography variant="body1">
            Select an owner to view their details.
          </Typography>
        )}
      </Box>

      {/* Charge View Popup */}
      {chargeViewOpen && (
        <PopUpWindow
          open={chargeViewOpen}
          onClose={handleCloseChargeView}
          title="Charge Details"
        >
          <ChargeForm
            selectedProperty={
              selectedCharge?.property._id
                ? selectedPropertyForCharge
                : ownerDetails?.properties.find(
                    (property) => property._id === selectedCharge?.property
                  )
            }
            selectedAmount={selectedCharge?.amount}
            selectedRef={selectedCharge?.reference}
            selectedModel={selectedCharge?.model}
            selectedInvoice={selectedCharge?.invoice}
            selectedAutoGenerated={selectedCharge?.auto_generated}
            selectedNotes={selectedCharge?.notes}
            readOnly={true}
          />
        </PopUpWindow>
      )}

      {fundTransactionPopupOpen && (
        <PopUpWindow
          onClose={() => setFundTransactionPopupOpen(false)}
          unfocusClose={true}
        >
          <NewFundTransactionForm
            onSuccess={() => setFundTransactionPopupOpen(false)}
            partner={ownerDetails}
          />
        </PopUpWindow>
      )}
      {/* Invoice Form Popup */}
      {invoiceFormOpen && (
        <PopUpWindow
          open={invoiceFormOpen}
          onClose={handleCloseInvoiceForm}
          title="Create Invoice"
        >
          <InvoiceForm
            charge={selectedChargeForInvoice}
            onInvoiceCreated={handleInvoiceCreated}
          />
        </PopUpWindow>
      )}

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => {
          if (!processingPayment) {
            setPaymentDialogOpen(false);
            setSelectedChargeForPayment(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {paymentSuccess ? "Payment Successful" : "Pay Charge"}
        </DialogTitle>
        <DialogContent>
          {paymentSuccess ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                my: 2,
              }}
            >
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" align="center">
                Payment successful!
              </Typography>
              <Typography color="text.secondary" align="center">
                The charge has been updated.
              </Typography>
            </Box>
          ) : (
            <>
              {selectedChargeForPayment && (
                <Box sx={{ my: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">
                        Property:{" "}
                        {typeof selectedChargeForPayment.property === "object"
                          ? selectedChargeForPayment.property.name
                          : "Property ID: " + selectedChargeForPayment.property}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">
                        Total Amount:{" "}
                        {formatCurrency(selectedChargeForPayment.amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">
                        Remaining Amount:{" "}
                        {formatCurrency(
                          selectedChargeForPayment.remaining_amount
                        )}
                      </Typography>
                    </Grid>
                    {selectedChargeForPayment.invoice && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Invoice:{" "}
                          {typeof selectedChargeForPayment.invoice === "object"
                            ? selectedChargeForPayment.invoice.invoice_number
                            : "Invoice ID: " + selectedChargeForPayment.invoice}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <TextField
                    margin="normal"
                    label="Payment Amount"
                    type="number"
                    fullWidth
                    value={paymentAmount}
                    onChange={(e) => {
                      setPaymentAmount(e.target.value);
                      // Clear any previous errors when user starts typing
                      setPaymentError("");
                    }}
                    inputProps={{
                      min: 0,
                      max: selectedChargeForPayment.remaining_amount,
                      step: "0.01",
                    }}
                    error={!!paymentError}
                    helperText={paymentError}
                    disabled={processingPayment}
                    sx={{ mt: 3 }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          parseFloat(paymentAmount) ===
                          parseFloat(selectedChargeForPayment.remaining_amount)
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPaymentAmount(
                              selectedChargeForPayment.remaining_amount.toString()
                            );
                            setPaymentError("");
                          }
                        }}
                        disabled={processingPayment}
                      />
                    }
                    label="Pay full remaining amount"
                  />
                </Box>
              )}
              {paymentError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {paymentError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {paymentSuccess ? (
            <Button
              onClick={() => {
                setPaymentDialogOpen(false);
                setSelectedChargeForPayment(null);
                setPaymentSuccess(false);
                // Refresh owner details to show updated balance and charge status
                fetchOwnerDetails(selectedOwner);
              }}
              variant="contained"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  if (!processingPayment) {
                    setPaymentDialogOpen(false);
                    setSelectedChargeForPayment(null);
                  }
                }}
                disabled={processingPayment}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  // Validate payment amount
                  if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
                    setPaymentError(
                      "Payment amount must be greater than zero."
                    );
                    return;
                  }

                  const amount = parseFloat(paymentAmount);
                  if (amount > selectedChargeForPayment.remaining_amount) {
                    setPaymentError(
                      "Payment amount cannot exceed the remaining amount."
                    );
                    return;
                  }

                  // Process payment
                  setProcessingPayment(true);
                  try {
                    const response = await axios.put(
                      `${apiUrl}/charge/pay/${selectedChargeForPayment._id}`,
                      { amount },
                      {
                        headers: {
                          Authorization: `Bearer ${user.token}`,
                          "Content-Type": "application/json",
                        },
                      }
                    );
                    setPaymentSuccess(true);
                  } catch (error) {
                    setPaymentError(
                      error.response?.data?.message ||
                        "Failed to process payment. Please try again."
                    );
                  } finally {
                    setProcessingPayment(false);
                  }
                }}
                disabled={
                  processingPayment ||
                  !paymentAmount ||
                  parseFloat(paymentAmount) <= 0
                }
                startIcon={
                  processingPayment ? <CircularProgress size={20} /> : null
                }
              >
                {processingPayment ? "Processing..." : "Pay Now"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </PageStructure>
  );
};

export default PreviewOwner;
